/**
 * DentalChartV2 — brand-new presentation layer of the Dental Chart.
 * Preserves all existing APIs (GET /patients/:id/dental-chart,
 * POST /patients/:id/dental-chart, PATCH notes, plan-treatment) — this
 * component only replaces the visual shell.  Old DentalChartTab remains
 * intact for reference.
 *
 * File split (2026-07):
 *   • useDentalSelection.js  — selection state + range/bulk/smart helpers
 *   • DentalChartV2.parts.jsx — Section, SurfaceBtn, SelectionToolbar,
 *                               TBtn, BulkActionsPanel + CATEGORIES/LEGEND
 * This file now owns only: server IO, save/mutation flow, and the render
 * tree that composes the parts.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { apiError } from "@/lib/apiError";
import { toast } from "sonner";
import {
  Sparkles, ZoomIn, ZoomOut, Plus, X, ChevronRight, ChevronLeft,
  ChevronDown, Notebook, History as HistoryIcon, Trash2, MoreVertical, Check,
} from "lucide-react";
import ToothV2, {
  toothInfoV2, CONDITION_META_V2 as META, CHART_COLORS,
  PERM_UPPER, PERM_LOWER, PRIM_UPPER, PRIM_LOWER,
} from "@/components/app/ToothSVGV2";
import { flattenChartForCopilot, copilotFindingsSummary } from "@/lib/copilotChart";
import { recommendTreatments, TIER_LABEL, stars } from "@/lib/treatmentRecommender";
import { useDentalSelection } from "@/components/app/useDentalSelection";
import {
  CATEGORIES, LEGEND, Section, SurfaceBtn, SelectionToolbar, BulkActionsPanel,
} from "@/components/app/DentalChartV2.parts";

const withDr = (n) => (/^dr\.?\s+/i.test(n || "") ? n : `Dr. ${n || "Doctor"}`);

export default function DentalChartV2({ patient }) {
  const [dentition, setDentition] = useState("permanent");
  const [zoom, setZoom] = useState(1);
  const [chart, setChart] = useState({ teeth: {}, notes: "" });
  const [treatments, setTreatments] = useState([]);
  const [team, setTeam] = useState([]);
  const [legendOpen, setLegendOpen] = useState(false);
  const [customOpen, setCustomOpen] = useState(false);
  const [customLabel, setCustomLabel] = useState("");
  const [customNote, setCustomNote]   = useState("");
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyRows, setHistoryRows] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  const upper = dentition === "primary" ? PRIM_UPPER : PERM_UPPER;
  const lower = dentition === "primary" ? PRIM_LOWER : PERM_LOWER;
  const allTeeth = useMemo(() => [...upper, ...lower], [upper, lower]);

  // ---- Multi-selection engine ---------------------------------------------
  // Extracted into a dedicated hook so the selection Set + Shift/Ctrl/Cmd
  // handlers + quadrant/arch/smart-filter helpers live in one testable
  // module instead of a 100-line block in the middle of this file.
  const {
    selectedTeeth, setSelectedTeeth,
    selectedTeethList, selectedTooth, isMultiSelect,
    selectedSurfaces, setSelectedSurfaces,
    selectedCounterOpen, setSelectedCounterOpen,
    selectOnly, toggleInSelection, clearSelection, handleToothClick,
    selectQuadrant, selectUpperArch, selectLowerArch, selectAllTeeth,
    invertSelection,
    smartFilterByCodes, selectAllHealthy, selectAllDeciduous,
  } = useDentalSelection({ allTeeth, chartTeeth: chart.teeth });

  // ---- Load chart + patient treatments + team ------------------------------
  const load = useCallback(async () => {
    if (!patient?.id) return;
    const [c, t, tm] = await Promise.all([
      api.get(`/patients/${patient.id}/dental-chart`).catch(() => ({ data: { teeth: {}, notes: "" } })),
      api.get(`/treatments?patient_id=${patient.id}`).catch(() => ({ data: [] })),
      api.get(`/team`).catch(() => ({ data: [] })),
    ]);
    setChart({ teeth: c.data?.teeth || {}, notes: c.data?.notes || "" });
    setTreatments(t.data || []);
    setTeam(tm.data || []);
  }, [patient?.id]);
  useEffect(() => { load(); }, [load]);

  const doctorName = (id) => {
    const t = team.find((x) => x.id === id);
    return t?.name ? withDr(t.name) : "";
  };

  // ---- Save helpers --------------------------------------------------------
  const addCondition = async (code) => {
    if (!selectedTooth) return toast.error("Select a tooth first");
    const t = chart.teeth[selectedTooth] || { conditions: [] };
    if (t.conditions.some((c) => c.code === code && !c.surfaces?.length)) return;
    // Optimistic update
    const optimistic = {
      ...chart,
      teeth: {
        ...chart.teeth,
        [selectedTooth]: {
          ...t,
          conditions: [...t.conditions, { code, surfaces: selectedSurfaces }],
        },
      },
    };
    setChart(optimistic);
    try {
      const { data } = await api.post(
        `/patients/${patient.id}/dental-chart/teeth/${selectedTooth}/conditions`,
        { code, surfaces: selectedSurfaces }
      );
      // Reconcile with server-assigned condition IDs
      setChart((prev) => ({
        ...prev,
        teeth: { ...prev.teeth, [selectedTooth]: data?.tooth || prev.teeth[selectedTooth] },
      }));
      toast.success(`${META[code]?.label || code} added to tooth ${selectedTooth}`);
    } catch (e) {
      toast.error(apiError(e, "Save failed"));
      load();
    }
  };

  const removeCondition = async (idx) => {
    if (!selectedTooth) return;
    const t = chart.teeth[selectedTooth] || { conditions: [] };
    const target = t.conditions[idx];
    if (!target) return;
    // Optimistic update
    setChart((prev) => ({
      ...prev,
      teeth: {
        ...prev.teeth,
        [selectedTooth]: {
          ...t,
          conditions: t.conditions.filter((_, i) => i !== idx),
        },
      },
    }));
    // Backend endpoint requires a server-issued condition id.
    if (!target.id) return; // Nothing to persist (was optimistic-only)
    try {
      const { data } = await api.delete(
        `/patients/${patient.id}/dental-chart/teeth/${selectedTooth}/conditions/${target.id}`
      );
      setChart((prev) => ({
        ...prev,
        teeth: { ...prev.teeth, [selectedTooth]: data?.tooth || prev.teeth[selectedTooth] },
      }));
    } catch (e) {
      toast.error(apiError(e, "Remove failed"));
      load();
    }
  };

  // ---- Custom condition (any diagnosis outside the fixed picker) ----------
  const submitCustomCondition = async () => {
    if (!selectedTooth) return toast.error("Select a tooth first");
    const label = (customLabel || "").trim();
    if (!label) return toast.error("Give the diagnosis a name");
    // Optimistic
    const t = chart.teeth[selectedTooth] || { conditions: [] };
    const optimistic = {
      code: "custom", label, note: customNote || "",
      surfaces: [...selectedSurfaces],
    };
    setChart((prev) => ({
      ...prev,
      teeth: {
        ...prev.teeth,
        [selectedTooth]: { ...t, conditions: [...(t.conditions || []), optimistic] },
      },
    }));
    try {
      const { data } = await api.post(
        `/patients/${patient.id}/dental-chart/teeth/${selectedTooth}/conditions`,
        { code: "custom", label, note: customNote, surfaces: selectedSurfaces }
      );
      setChart((prev) => ({
        ...prev,
        teeth: { ...prev.teeth, [selectedTooth]: data?.tooth || prev.teeth[selectedTooth] },
      }));
      toast.success(`Diagnosis "${label}" added to tooth ${selectedTooth}`);
      setCustomOpen(false); setCustomLabel(""); setCustomNote("");
    } catch (e) {
      toast.error(apiError(e, "Save failed"));
      load();
    }
  };

  // ---- Tooth timeline (chart history + treatments) ------------------------
  const openHistory = async () => {
    if (!selectedTooth) return;
    setHistoryOpen(true);
    setHistoryLoading(true);
    try {
      const { data } = await api.get(
        `/patients/${patient.id}/dental-chart/teeth/${selectedTooth}/history`
      );
      setHistoryRows(data?.history || []);
    } catch (e) {
      toast.error(apiError(e, "History failed"));
      setHistoryRows([]);
    } finally {
      setHistoryLoading(false);
    }
  };

  // ---- Selection navigation -----------------------------------------------
  const gotoTooth = (dir) => {
    if (!selectedTooth) return;
    const idx = allTeeth.indexOf(Number(selectedTooth));
    if (idx < 0) return;
    const next = allTeeth[(idx + dir + allTeeth.length) % allTeeth.length];
    selectOnly(String(next));
  };

  const toggleSurface = (s) =>
    setSelectedSurfaces((prev) => prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s]);
  // "All surfaces" toggle — snaps selectedSurfaces to the full set B/L/M/D/O
  // or clears it if already full. Called from the Surfaces block.
  const ALL_SURFACES = ["B","L","M","D","O"];
  const allSurfacesActive = ALL_SURFACES.every((s) => selectedSurfaces.includes(s));
  const toggleAllSurfaces = () =>
    setSelectedSurfaces(allSurfacesActive ? [] : [...ALL_SURFACES]);

  // ---- Bulk condition apply/remove across the whole selection ------------
  // Uses the existing per-tooth POST/DELETE endpoints (no new backend). We
  // fire them in parallel and reconcile the full chart from the server once
  // done so the UI can't drift from persistence.
  const bulkAddCondition = useCallback(async (code) => {
    if (!selectedTeethList.length) return;
    const surfaces = [...selectedSurfaces]; // same surfaces applied everywhere
    const results = await Promise.allSettled(
      selectedTeethList.map((tid) =>
        api.post(`/patients/${patient.id}/dental-chart/teeth/${tid}/conditions`,
                 { code, surfaces })
      )
    );
    const ok = results.filter((r) => r.status === "fulfilled").length;
    const fail = results.length - ok;
    if (ok) toast.success(`${META[code]?.label || code} added to ${ok} ${ok === 1 ? "tooth" : "teeth"}`);
    if (fail) toast.error(`${fail} update(s) failed — refreshing`);
    load();
  }, [selectedTeethList, selectedSurfaces, patient?.id, load]);

  const bulkRemoveCondition = useCallback(async (code) => {
    if (!selectedTeethList.length) return;
    // For each tooth, find the first condition with matching code and delete
    // its server-side id. Optimistically nothing — reconcile on completion.
    const jobs = [];
    for (const tid of selectedTeethList) {
      const t = chart.teeth?.[tid];
      if (!t) continue;
      for (const c of (t.conditions || [])) {
        if (c.code === code && c.id) {
          jobs.push(api.delete(
            `/patients/${patient.id}/dental-chart/teeth/${tid}/conditions/${c.id}`
          ));
        }
      }
    }
    if (!jobs.length) { toast.message(`No ${META[code]?.label || code} to remove.`); return; }
    const results = await Promise.allSettled(jobs);
    const ok = results.filter((r) => r.status === "fulfilled").length;
    toast.success(`Removed ${META[code]?.label || code} from ${ok} tooth/teeth`);
    load();
  }, [selectedTeethList, chart, patient?.id, load]);

  // ---- Keyboard shortcuts -------------------------------------------------
  // Global handlers ONLY when the chart is mounted. We deliberately avoid
  // stealing focus while the user is typing into inputs/textareas, which is
  // the standard escape hatch for "Ctrl+A shouldn't select every tooth
  // while I'm writing notes".
  useEffect(() => {
    const isTypingTarget = (el) => {
      if (!el) return false;
      const tag = (el.tagName || "").toLowerCase();
      return tag === "input" || tag === "textarea" || el.isContentEditable;
    };
    const onKey = (e) => {
      if (isTypingTarget(document.activeElement)) return;
      // Ctrl/Cmd + A → select all teeth in current dentition
      if ((e.ctrlKey || e.metaKey) && (e.key === "a" || e.key === "A")) {
        e.preventDefault();
        selectAllTeeth();
        return;
      }
      // Esc → clear selection
      if (e.key === "Escape") {
        if (selectedTeeth.size > 0) {
          e.preventDefault();
          clearSelection();
        }
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [selectedTeeth, clearSelection, selectAllTeeth]);

  const sel = selectedTooth ? chart.teeth[selectedTooth] || { conditions: [] } : null;
  const selInfo = selectedTooth ? toothInfoV2(selectedTooth) : null;
  const selPrimaryCode = sel?.conditions?.find((c) => META[c.code]?.label && !c.surfaces?.length)?.code
                       || sel?.conditions?.[0]?.code
                       || null;
  const primaryStatusLabel = selPrimaryCode ? META[selPrimaryCode]?.label : "Healthy";
  const primaryStatusColor = selPrimaryCode ? META[selPrimaryCode]?.color : "#3F7A4A";

  const patientTreatments = useMemo(
    () => (treatments || []).filter((t) => !selectedTooth || String(t.tooth) === String(selectedTooth) || !t.tooth),
    [treatments, selectedTooth]
  );

  // ---- Chart → Treatment URL builder --------------------------------------
  // Carries tooth, selected surfaces, ALL diagnoses (locked on treatment page),
  // primary diagnosis (for chart back-sync), procedure/variant if a specific
  // suggestion was clicked, and a chart_ref so the Treatments page opens with
  // a fully pre-filled draft.
  const buildAddTreatmentHref = useCallback((suggestion) => {
    if (!selectedTooth) return `/app/treatments?patient_id=${patient.id}`;
    const params = new URLSearchParams();
    params.set("patient_id", patient.id);
    params.set("tooth", selectedTooth);
    if (selectedSurfaces?.length) params.set("surfaces", selectedSurfaces.join(","));
    if (selPrimaryCode)           params.set("diagnosis", selPrimaryCode);
    // Full set of diagnoses (locked read-only on Treatments page).
    const allCodes = Array.from(new Set(
      (chart.teeth[selectedTooth]?.conditions || []).map((c) => c.code)
    )).filter(Boolean);
    if (allCodes.length) params.set("diagnoses", allCodes.join(","));
    params.set("chart_ref", `tooth-${selectedTooth}`);
    if (suggestion?.procedure) params.set("procedure", suggestion.procedure);
    if (suggestion?.variant)   params.set("variant", suggestion.variant);
    return `/app/treatments?${params.toString()}`;
  }, [patient?.id, selectedTooth, selectedSurfaces, selPrimaryCode, chart]);

  // ---- Smart treatment suggestions (multi-diagnosis aware) ----------------
  const suggestions = useMemo(
    () => recommendTreatments(sel?.conditions || []).slice(0, 5),
    [sel]
  );

  // Copilot pre-seed — flattens the chart into {tooth, surfaces, condition}
  // rows so Claude can reason surface-by-surface, then wraps into a natural
  // prompt asking for a treatment plan.
  const copilotSeed = useMemo(() => {
    const findings = flattenChartForCopilot(chart, META);
    if (!findings.length) return "";
    const summary = copilotFindingsSummary(findings);
    return `Here is the current dental chart for this patient. ${summary} `
         + `Please suggest a prioritised treatment plan (with tooth number, `
         + `recommended procedure, urgency and rough visit count).`;
  }, [chart]);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_360px] gap-6"
         data-testid="dental-chart-v2">
      {/* ============================ MAIN CARD ============================ */}
      <div className="rounded-3xl bg-white border border-[#E8EAE9] shadow-sm p-6">
        {/* Hero title */}
        <div className="mb-6">
          <div className="text-[10px] uppercase tracking-[0.3em] text-ink-soft">FDI Dental Chart</div>
          <div className="font-display text-3xl text-ink mt-1">
            {dentition === "primary" ? "Primary dentition" :
             dentition === "mixed"   ? "Mixed dentition"   : "Permanent dentition"}
          </div>
        </div>

        {/* Toolbar row */}
        <div className="flex items-center justify-between gap-3 mb-6 flex-wrap">
          <div className="flex items-center gap-1 bg-linen/60 rounded-full p-1 text-xs" data-testid="v2-dentition-toggle">
            <Link
              to={`/app/ai?patient=${patient.id}${copilotSeed ? `&q=${encodeURIComponent(copilotSeed)}` : ""}`}
              data-testid="v2-ask-copilot"
              className="px-3 py-1.5 rounded-full text-ink hover:bg-white/70 transition inline-flex items-center gap-1.5">
              <Sparkles className="w-3.5 h-3.5 text-clay" /> Ask Copilot
            </Link>
            {["permanent", "mixed", "primary"].map((v) => (
              <button key={v}
                onClick={() => setDentition(v)}
                data-testid={`v2-dentition-${v}`}
                className={`px-4 py-1.5 rounded-full transition font-medium ${
                  dentition === v ? "bg-clay text-linen shadow-sm" : "text-ink-mid hover:text-ink hover:bg-white/60"
                }`}>
                {v[0].toUpperCase() + v.slice(1)}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-1 bg-white border border-[#E8EAE9] rounded-full p-1" data-testid="v2-zoom-controls">
            <button onClick={() => setZoom((z) => Math.max(0.6, +(z - 0.15).toFixed(2)))}
                    data-testid="v2-zoom-out"
                    className="p-1.5 rounded-full text-ink-mid hover:bg-linen" title="Zoom out"><ZoomOut className="w-3.5 h-3.5" /></button>
            <button onClick={() => setZoom((z) => Math.min(1.8, +(z + 0.15).toFixed(2)))}
                    data-testid="v2-zoom-in"
                    className="p-1.5 rounded-full text-ink-mid hover:bg-linen" title="Zoom in"><ZoomIn className="w-3.5 h-3.5" /></button>
            <span className="text-xs text-ink-mid px-2.5 select-none font-mono">{Math.round(zoom * 100)}%</span>
            <button onClick={() => setZoom(1)} data-testid="v2-zoom-reset"
                    className="px-3 py-1 rounded-full text-xs text-ink hover:bg-linen">Reset</button>
          </div>
        </div>

        {/* Selection Toolbar — quadrants, arches, smart filters, invert,
            deselect. Everything below the chart syncs from the same
            `selectedTeeth` Set so any toolbar action is instantly reflected
            on every tooth as well as in the counter + right panel. */}
        <SelectionToolbar
          count={selectedTeeth.size}
          counterOpen={selectedCounterOpen}
          onToggleCounter={() => setSelectedCounterOpen((v) => !v)}
          selectedList={selectedTeethList}
          onRemoveOne={(t) => setSelectedTeeth((prev) => {
            const next = new Set(prev); next.delete(t); return next;
          })}
          onQuadrant={selectQuadrant}
          onUpperArch={selectUpperArch}
          onLowerArch={selectLowerArch}
          onSelectAll={selectAllTeeth}
          onInvert={invertSelection}
          onClear={clearSelection}
          onSmartCaries={() => smartFilterByCodes(["caries"])}
          onSmartRCT={() => smartFilterByCodes(["rct","pulpectomy","pulpotomy"])}
          onSmartCrown={() => smartFilterByCodes(["crown"])}
          onSmartImplant={() => smartFilterByCodes(["implant"])}
          onSmartMissing={() => smartFilterByCodes(["missing","extraction"])}
          onSmartFilled={() => smartFilterByCodes(["composite","gic","amalgam","filling"])}
          onSmartFracture={() => smartFilterByCodes(["fracture"])}
          onSmartMobility={() => smartFilterByCodes(["mobility"])}
          onSmartHealthy={selectAllHealthy}
          onSmartDeciduous={selectAllDeciduous}
        />

        {/* Chart */}
        <div className="rounded-2xl bg-white border border-[#EDEEEE] p-6 overflow-x-auto">
          <div className="mx-auto" style={{ width: "fit-content", transform: `scale(${zoom})`, transformOrigin: "top center" }}>
            <div className="text-center text-[10px] uppercase tracking-[0.3em] text-ink-soft mb-3">
              Maxillary (upper arch)
            </div>
            {/* Upper — FDI numbers row above; teeth flipped roots-up */}
            <div className="grid gap-1 justify-items-center"
                 style={{ gridTemplateColumns: `repeat(${upper.length}, minmax(46px, 1fr))` }}>
              {upper.map((t) => (
                <div key={`nu-${t}`} className="text-[11px] font-mono text-ink-mid select-none">{t}</div>
              ))}
              {upper.map((t) => (
                <div key={`tu-${t}`} style={{ transform: "scaleY(-1)" }}>
                  <ToothV2 tooth={t}
                    conditions={chart.teeth[t]?.conditions || []}
                    isSelected={selectedTeeth.has(String(t))}
                    selectedSurfaces={selectedTooth === String(t) ? selectedSurfaces : []}
                    onSelect={handleToothClick} />
                </div>
              ))}
            </div>

            {/* Occlusal divider */}
            <div className="my-6 h-px bg-[repeating-linear-gradient(90deg,_#D6D9D8_0_6px,_transparent_6px_12px)]" />

            {/* Lower — teeth normal, then FDI numbers below */}
            <div className="grid gap-1 justify-items-center"
                 style={{ gridTemplateColumns: `repeat(${lower.length}, minmax(46px, 1fr))` }}>
              {lower.map((t) => (
                <ToothV2 key={`tl-${t}`} tooth={t}
                  conditions={chart.teeth[t]?.conditions || []}
                  isSelected={selectedTeeth.has(String(t))}
                  selectedSurfaces={selectedTooth === String(t) ? selectedSurfaces : []}
                  onSelect={handleToothClick} />
              ))}
              {lower.map((t) => (
                <div key={`nl-${t}`} className="text-[11px] font-mono text-ink-mid select-none">{t}</div>
              ))}
            </div>
            <div className="text-center text-[10px] uppercase tracking-[0.3em] text-ink-soft mt-3">
              Mandibular (lower arch)
            </div>
          </div>
        </div>

        {/* Legend — exactly 10 items in 5 cols × 2 rows, matching reference */}
        <div className="mt-6 rounded-2xl border border-[#E8EAE9] bg-white" data-testid="v2-legend">
          <button
            type="button"
            onClick={() => setLegendOpen((o) => !o)}
            className="w-full px-5 py-3 flex items-center gap-2 text-sm text-ink hover:bg-linen/40 rounded-2xl">
            <ChevronDown className={`w-4 h-4 text-ink-mid transition-transform ${legendOpen ? "rotate-180" : ""}`} />
            <span className="font-medium">Legend</span>
            <span className="text-ink-soft">({LEGEND.length})</span>
          </button>
          {legendOpen && (
            <div className="px-5 pb-4 pt-1 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-x-6 gap-y-3">
              {LEGEND.map((L) => (
                <div key={L.code} className="flex items-center gap-2 text-xs text-ink-mid">
                  <span className="inline-block w-1 h-4 rounded-sm shrink-0" style={{ background: META[L.code]?.color || "#5B6660" }} />
                  <span>{L.label}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ============================ RIGHT PANEL ============================ */}
      <aside className="rounded-3xl bg-white border border-[#E8EAE9] shadow-sm p-5"
             data-testid="v2-right-panel">
        {isMultiSelect ? (
          /* Bulk-actions panel — replaces the single-tooth clinical view
             whenever 2+ teeth are selected. Applies conditions across the
             entire selection in one go via the existing add/remove endpoints. */
          <BulkActionsPanel
            teeth={selectedTeethList}
            onClear={clearSelection}
            onInvert={invertSelection}
            onBulkAdd={(code) => bulkAddCondition(code)}
            onBulkRemove={(code) => bulkRemoveCondition(code)}
            onRemoveOne={(t) => setSelectedTeeth((prev) => {
              const next = new Set(prev); next.delete(t); return next;
            })}
          />
        ) : !selectedTooth ? (
          <div className="p-4 text-center text-ink-soft">
            <div className="text-sm">Tap any tooth to open its clinical panel.</div>
            <div className="text-[11px] mt-2 opacity-80">
              Ctrl/Cmd + click for multi-select · Shift + click for a range ·
              use the toolbar above the chart for quadrant / arch selections.
            </div>
          </div>
        ) : (
          <>
            {/* Tooth header */}
            <div className="flex items-start justify-between gap-2">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-ink-soft">Tooth</div>
                <div className="font-display text-3xl leading-none text-ink" data-testid="v2-tooth-num">{selectedTooth}</div>
                <div className="text-[11px] text-ink-mid mt-1">
                  {selInfo?.quadrantLabel} · {selInfo?.name}
                </div>
              </div>
              <div className="flex items-center gap-1">
                <button data-testid="v2-prev-tooth" onClick={() => gotoTooth(-1)}
                        className="w-7 h-7 rounded-full grid place-items-center text-ink-mid hover:bg-linen">
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <button data-testid="v2-next-tooth" onClick={() => gotoTooth(1)}
                        className="w-7 h-7 rounded-full grid place-items-center text-ink-mid hover:bg-linen">
                  <ChevronRight className="w-4 h-4" />
                </button>
                <button data-testid="v2-close-panel" onClick={clearSelection}
                        className="w-7 h-7 rounded-full grid place-items-center text-ink-mid hover:bg-linen">
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Status */}
            <Section label="Status">
              <span
                data-testid="v2-status-pill"
                className="inline-flex items-center gap-1.5 text-xs px-3 py-1 rounded-full uppercase tracking-widest"
                style={{ background: primaryStatusColor + "22", color: primaryStatusColor }}
              >
                {primaryStatusLabel}
              </span>
            </Section>

            {/* Surfaces — radial layout: B top, L bottom, M left, D right, O center */}
            <Section label="Surfaces">
              <div className="relative mx-auto" style={{ width: 200, height: 160 }}>
                <SurfaceBtn label="B" testid="v2-surface-B" style={{ top: 0, left: "50%", transform: "translateX(-50%)" }} active={selectedSurfaces.includes("B")} onClick={() => toggleSurface("B")} />
                <SurfaceBtn label="L" testid="v2-surface-L" style={{ bottom: 0, left: "50%", transform: "translateX(-50%)" }} active={selectedSurfaces.includes("L")} onClick={() => toggleSurface("L")} />
                <SurfaceBtn label="M" testid="v2-surface-M" style={{ left: 0, top: "50%", transform: "translateY(-50%)" }} active={selectedSurfaces.includes("M")} onClick={() => toggleSurface("M")} />
                <SurfaceBtn label="D" testid="v2-surface-D" style={{ right: 0, top: "50%", transform: "translateY(-50%)" }} active={selectedSurfaces.includes("D")} onClick={() => toggleSurface("D")} />
                <SurfaceBtn label="O" testid="v2-surface-O" style={{ left: "50%", top: "50%", transform: "translate(-50%,-50%)" }} active={selectedSurfaces.includes("O")} onClick={() => toggleSurface("O")} />
              </div>
              <div className="mt-3 flex justify-center">
                <button
                  type="button"
                  onClick={toggleAllSurfaces}
                  data-testid="v2-surface-all"
                  data-active={allSurfacesActive ? "true" : "false"}
                  className={`inline-flex items-center gap-1.5 text-[11px] px-3 py-1.5 rounded-full border transition ${
                    allSurfacesActive
                      ? "bg-clay text-linen border-clay shadow"
                      : "bg-white text-ink-mid border-[#E8EAE9] hover:bg-linen"
                  }`}
                >
                  <Check className={`w-3 h-3 ${allSurfacesActive ? "" : "opacity-0"}`} />
                  All surfaces
                </button>
              </div>
            </Section>

            {/* Conditions */}
            <Section label="Conditions">
              {(!sel?.conditions || sel.conditions.length === 0) ? (
                <div className="text-xs text-ink-soft italic">None</div>
              ) : (
                <div className="flex flex-wrap gap-1.5" data-testid="v2-conditions-list">
                  {sel.conditions.map((c, i) => (
                    <span key={`${c.code}-${(c.surfaces || []).join("")}-${c.label || ""}-${i}`}
                          className="inline-flex items-center gap-1.5 text-[11px] px-2.5 py-1 rounded-full"
                          style={{ background: (META[c.code]?.color || "#5B6660") + "22", color: META[c.code]?.color || "#5B6660" }}>
                      {c.code === "custom" ? (c.label || "Custom") : (META[c.code]?.label || c.code)}
                      {c.surfaces?.length > 0 && <span className="opacity-70">· {c.surfaces.join("")}</span>}
                      <button onClick={() => removeCondition(i)} className="opacity-60 hover:opacity-100" aria-label="Remove condition">
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </Section>

            {/* Treatments */}
            <Section label="Treatments">
              {patientTreatments.length === 0 ? (
                <div className="text-xs text-ink-soft italic">No treatments logged for this tooth.</div>
              ) : (
                <div className="space-y-1.5" data-testid="v2-treatments-list">
                  {patientTreatments.slice(0, 4).map((t) => (
                    <div key={t.id} className="flex items-center gap-2 text-xs">
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#FDF3ED] text-[#8A3F1E]">
                        {t.name || t.procedure || "Treatment"}
                      </span>
                      <span className="text-ink-mid">{t.date ? new Date(t.date).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : ""}</span>
                      <span className="text-ink-soft ml-auto truncate">{doctorName(t.doctor_id)}</span>
                      <button className="text-ink-soft hover:text-ink" aria-label="Row actions"><MoreVertical className="w-3.5 h-3.5" /></button>
                    </div>
                  ))}
                </div>
              )}
            </Section>

            {/* Smart treatment suggestions — ranked, multi-diagnosis aware */}
            {suggestions.length > 0 && (
              <Section label="Suggested treatments">
                <div className="space-y-1.5" data-testid="v2-suggestions">
                  {suggestions.map((s) => {
                    const tierColor =
                      s.tier === "recommended" ? "#8A3F1E" :
                      s.tier === "alternative" ? "#2C58A6" :
                      s.tier === "long_term"   ? "#3F7A4A" :
                                                 "#5B6660";
                    return (
                      <Link
                        key={`${s.procedure}-${s.variant || ""}`}
                        to={buildAddTreatmentHref(s)}
                        data-testid={`v2-suggest-${s.procedure.toLowerCase().replace(/[^a-z0-9]+/g, "-")}${s.variant ? "-" + s.variant.toLowerCase() : ""}`}
                        className="block rounded-lg border border-[#E8EAE9] hover:border-clay/50 hover:bg-linen px-2.5 py-2 transition"
                        title={`Create ${s.procedure}${s.variant ? " · " + s.variant : ""}`}
                      >
                        <div className="flex items-center gap-2">
                          <span
                            className="text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded"
                            style={{ background: tierColor + "18", color: tierColor }}
                          >
                            {TIER_LABEL[s.tier]}
                          </span>
                          <span className="text-xs font-medium text-ink">
                            {s.procedure}{s.variant ? ` · ${s.variant}` : ""}
                          </span>
                          <span className="ml-auto text-[10px] text-[#D0A94A]" title={`${s.stars}/5`}>
                            {stars(s.stars)}
                          </span>
                        </div>
                        {s.reason && (
                          <div className="text-[10px] text-ink-mid mt-1 leading-snug">
                            {s.reason}
                          </div>
                        )}
                      </Link>
                    );
                  })}
                </div>
              </Section>
            )}

            {/* Categorised condition picker — Phase 4 (four groups + colour system) */}
            {CATEGORIES.map((cat) => {
              const activeCodes = new Set((sel?.conditions || []).map((c) => c.code));
              return (
                <Section key={cat.key} label={cat.label}>
                  <div className="grid grid-cols-2 gap-1.5" data-testid={`v2-cat-${cat.key}`}>
                    {cat.codes.map((code) => {
                      const on = activeCodes.has(code);
                      return (
                        <button
                          key={code}
                          type="button"
                          onClick={() => on ? removeCondition((sel?.conditions || []).findIndex((c) => c.code === code)) : addCondition(code)}
                          data-testid={`v2-cond-${code}`}
                          className={`text-left text-xs px-2.5 py-1.5 rounded-lg border transition inline-flex items-center gap-2 ${
                            on ? "border-transparent shadow-sm" : "border-[#E8EAE9] hover:bg-linen"
                          }`}
                          style={on ? { background: cat.tint + "15", color: cat.tint } : undefined}
                        >
                          <span
                            className={`w-3.5 h-3.5 rounded border ${on ? "border-transparent" : "border-[#D6D9D8]"}
                                        grid place-items-center`}
                            style={on ? { background: cat.tint } : undefined}
                          >
                            {on && <Check className="w-2.5 h-2.5 text-white" />}
                          </span>
                          {META[code]?.label || code}
                        </button>
                      );
                    })}
                  </div>
                </Section>
              );
            })}

            {/* Custom condition — for anything outside the picker */}
            <div className="mt-4 pt-3 border-t border-[#E8EAE9]" data-testid="v2-custom-condition-block">
              {!customOpen ? (
                <button
                  type="button"
                  data-testid="v2-add-custom-condition"
                  onClick={() => setCustomOpen(true)}
                  className="w-full inline-flex items-center justify-center gap-2 rounded-xl border border-dashed border-[#D6D9D8] text-xs text-ink-mid px-4 py-2 hover:border-clay/50 hover:text-ink hover:bg-linen"
                >
                  <Plus className="w-3.5 h-3.5" /> Add condition (custom diagnosis)
                </button>
              ) : (
                <div className="space-y-2">
                  <div className="text-[10px] uppercase tracking-widest text-ink-soft">Custom diagnosis</div>
                  <input
                    data-testid="v2-custom-label"
                    autoFocus
                    value={customLabel}
                    onChange={(e) => setCustomLabel(e.target.value)}
                    placeholder="e.g. Cracked tooth syndrome"
                    className="w-full rounded-lg border border-[#E8EAE9] px-3 py-2 text-xs"
                  />
                  <input
                    data-testid="v2-custom-note"
                    value={customNote}
                    onChange={(e) => setCustomNote(e.target.value)}
                    placeholder="Optional clinical note"
                    className="w-full rounded-lg border border-[#E8EAE9] px-3 py-2 text-xs"
                  />
                  <div className="flex items-center gap-1.5">
                    <button
                      type="button"
                      data-testid="v2-custom-save"
                      onClick={submitCustomCondition}
                      className="flex-1 rounded-lg bg-clay text-linen text-xs px-3 py-2 hover:brightness-110"
                    >
                      Save diagnosis
                    </button>
                    <button
                      type="button"
                      data-testid="v2-custom-cancel"
                      onClick={() => { setCustomOpen(false); setCustomLabel(""); setCustomNote(""); }}
                      className="rounded-lg border border-[#E8EAE9] text-xs px-3 py-2 hover:bg-linen"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Add treatment CTA — carries full chart context to Treatments */}
            <div className="mt-4">
              <Link
                to={buildAddTreatmentHref()}
                data-testid="v2-add-treatment"
                className="w-full inline-flex items-center justify-center gap-2 rounded-xl bg-clay text-linen text-sm px-4 py-2.5 hover:brightness-110 shadow">
                <Plus className="w-4 h-4" /> Add treatment
              </Link>
            </div>

            {/* Notes / History footer */}
            <div className="mt-5 pt-3 border-t border-[#E8EAE9] flex items-center gap-2 text-xs text-ink-mid">
              <button className="inline-flex items-center gap-1 px-2 py-1 rounded-lg hover:bg-linen"><Notebook className="w-3.5 h-3.5" /> Notes</button>
              <button
                data-testid="v2-open-history"
                onClick={openHistory}
                className="inline-flex items-center gap-1 px-2 py-1 rounded-lg hover:bg-linen">
                <HistoryIcon className="w-3.5 h-3.5" /> History
              </button>
              <ChevronRight className="w-3.5 h-3.5 ml-auto opacity-40" />
            </div>
          </>
        )}
      </aside>

      {/* Tooth timeline modal — history + treatments merged, most recent first */}
      {historyOpen && (
        <div
          data-testid="v2-history-overlay"
          className="fixed inset-0 z-50 bg-ink/40 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setHistoryOpen(false)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-lg rounded-2xl bg-white border border-[#E8EAE9] shadow-xl overflow-hidden"
            data-testid="v2-history-modal"
          >
            <div className="px-5 py-3 border-b border-[#E8EAE9] flex items-center justify-between">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-ink-soft">Tooth timeline</div>
                <div className="font-display text-lg text-ink">Tooth {selectedTooth}</div>
              </div>
              <button data-testid="v2-history-close" onClick={() => setHistoryOpen(false)}
                      className="w-7 h-7 rounded-full grid place-items-center text-ink-mid hover:bg-linen">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="max-h-[65vh] overflow-y-auto p-5" data-testid="v2-history-list">
              {historyLoading ? (
                <div className="text-xs text-ink-soft">Loading…</div>
              ) : historyRows.length === 0 ? (
                <div className="text-xs text-ink-soft italic">No history recorded yet.</div>
              ) : (
                <ul className="space-y-3">
                  {historyRows.map((h, i) => (
                    <li key={h.id || `${h.event || "evt"}-${h.at || h.created_at || i}`} className="flex items-start gap-3 text-xs">
                      <div className="w-1 h-1 rounded-full bg-clay mt-1.5 shrink-0" />
                      <div className="flex-1">
                        <div className="text-ink">
                          {(h.event || "").replaceAll("_", " ")}
                          {h.meta?.procedure && (
                            <span className="text-ink-mid"> — {h.meta.procedure}</span>
                          )}
                          {h.meta?.code && (
                            <span className="text-ink-mid"> — {META[h.meta.code]?.label || h.meta.code}</span>
                          )}
                        </div>
                        <div className="text-ink-soft mt-0.5 font-mono">
                          {h.at ? new Date(h.at).toLocaleString("en-IN", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : ""}
                          {h.actor ? ` · ${h.actor}` : ""}
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

